/**
 * VisitaMed Dashboard View
 * Tela principal do aplicativo
 */

import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { liveQuery, type Subscription } from 'dexie';
import { navigate, getCurrentRoute } from '@/router/router';
import { getAllNotes, deleteNotes } from '@/services/db/notes-service';
import {
  getCurrentUserVisitMember,
  fetchVisitMembersFromRemote,
  listVisitMembers,
  removeVisitMemberAsAdmin,
  updateVisitMemberRole,
  type RemoveVisitMemberResult,
  type UpdateVisitMemberRoleResult,
} from '@/services/db/visit-members-service';
import {
  getVisitById,
  isVisitExpiredLocally,
  deletePrivateVisit,
  leaveVisit,
  deleteGroupVisitAsOwner,
  ensureVisitIsGroup,
  duplicateVisitAsPrivate,
} from '@/services/db/visits-service';
import { createVisitInviteForVisit, buildVisitInviteLink } from '@/services/db/visit-invites-service';
import {
  canEditNote,
  canDeleteNote,
  canDuplicateVisit,
  getVisitAccessState,
  type VisitAccessState,
} from '@/services/auth/visit-permissions';
import {
  getDashboardGroupActions,
  canOpenParticipantsPanel,
  getParticipantDisplayName,
  getParticipantRoleLabel,
  getParticipantRowActions,
} from '@/services/auth/dashboard-actions-policy';
import { groupNotesByTag } from '@/utils/group-notes-by-tag';
import { getSyncStatus, subscribeToSync, type SyncStatus } from '@/services/sync/sync-service';
import { generateMessage, copyToClipboard, type ExportScope } from '@/services/export/message-export';
import type { Note } from '@/models/note';
import type { Visit } from '@/models/visit';
import type { VisitMember, VisitRole } from '@/models/visit-member';
import type { InviteRole } from '@/models/visit-invite';
import type { DashboardAction, ParticipantRowAction } from '@/services/auth/dashboard-actions-policy';
import '../components/base/fab-button';
import '../components/groups/tag-group';
import '../components/feedback/action-sheet';
import '../components/feedback/sync-status-bar';



/** Tipo de escopo selecionado */
type SelectedScope =
  | { type: 'tag'; tag: string; notes: Note[] }
  | { type: 'visit'; notes: Note[] }
  | null;

/** Configuração do modal genérico de confirmação de ação sobre participante */
interface ParticipantConfirmModalConfig {
  title: string;
  description: string;
  error: string;
  isProcessing: boolean;
  confirmButtonClass: string;
  confirmLabel: string;
  processingLabel: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

@customElement('dashboard-view')
export class DashboardView extends LitElement {
  @state() private visitId: string | null = null;
  // S12A: visitName não usado - título fixo do app
  @state() private notes: Note[] = [];
  @state() private isLoading = true;
  @state() private isActionSheetOpen = false;
  @state() private selectedScope: SelectedScope = null;
  @state() private selectedTitle = '';
  @state() private showToast = false;
  @state() private toastMessage = '';
  @state() private isPreviewOpen = false;
  @state() private previewMessage = '';
  @state() private member: VisitMember | null = null;
  @state() private currentVisit: Visit | null = null;
  @state() private isVisitExpired = false;
  @state() private accessState: VisitAccessState = 'no-membership';
  @state() private actions: DashboardAction[] = [];
  @state() private isDeleteConfirmOpen = false;
  @state() private isVisitDeleteConfirmOpen = false;
  @state() private isLeaveVisitConfirmOpen = false;
  @state() private isDeletingVisit = false;
  @state() private isLeavingVisit = false;
  @state() private visitDeleteError = '';
  @state() private leaveVisitError = '';
  @state() private isInviteModalOpen = false;
  @state() private inviteRole: InviteRole = 'editor';
  @state() private inviteLink = '';
  @state() private isGeneratingInvite = false;
  @state() private isDuplicatingVisit = false;
  @state() private isParticipantsModalOpen = false;
  @state() private participants: VisitMember[] = [];
  @state() private isLoadingParticipants = false;
  @state() private participantsFromCache = false;
  @state() private isChangingParticipantRole = false;
  @state() private roleChangeTarget: { member: VisitMember; action: 'promote' | 'demote' } | null = null;
  @state() private roleChangeError = '';
  @state() private removeParticipantTarget: VisitMember | null = null;
  @state() private isRemovingParticipant = false;
  @state() private removeParticipantError = '';
  @state() private syncStatus: SyncStatus = getSyncStatus();
  @state() private lastSyncErrorAt: Date | null = null;

  private notesSubscription: Subscription | null = null;
  private syncStatusUnsubscribe: (() => void) | null = null;

  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();

    // Lê visitId da rota
    const route = getCurrentRoute();
    if (route?.params['visitId']) {
      this.visitId = route.params['visitId'];
      await Promise.all([this.loadMember(), this.loadVisit()]);
    }

    this.startNotesSubscription();
    this.startSyncStatusSubscription();
  }

  // S12A: loadVisitName removido - título fixo do app
  private async loadMember(): Promise<void> {
    if (!this.visitId) return;

    try {
      this.member = (await getCurrentUserVisitMember(this.visitId)) ?? null;
      this.accessState = getVisitAccessState(this.member);
      // Atualiza ações do action sheet baseada na permissão
      const canDelete = this.member ? canDeleteNote(this.member) : false;
      this.actions = getDashboardGroupActions(canDelete);
    } catch {
      // Usuário não autenticado ou erro - sem membership
      this.member = null;
      this.accessState = 'no-membership';
      this.actions = getDashboardGroupActions(false);
    }
  }

  private async loadVisit(): Promise<void> {
    if (!this.visitId) return;

    try {
      const visit = await getVisitById(this.visitId);
      this.currentVisit = visit ?? null;
      this.isVisitExpired = !visit && await isVisitExpiredLocally(this.visitId);
    } catch {
      this.currentVisit = null;
      this.isVisitExpired = false;
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.notesSubscription?.unsubscribe();
    this.notesSubscription = null;
    this.syncStatusUnsubscribe?.();
    this.syncStatusUnsubscribe = null;
  }

  private startNotesSubscription(): void {
    this.isLoading = true;

    this.notesSubscription?.unsubscribe();

    if (!this.visitId) {
      this.notes = [];
      this.isLoading = false;
      return;
    }

    const visitId = this.visitId;
    this.notesSubscription = liveQuery(() => getAllNotes(visitId)).subscribe({
      next: (notes) => {
        this.notes = notes;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar notas:', error);
        this.notes = [];
        this.isLoading = false;
      },
    });
  }

  private startSyncStatusSubscription(): void {
    this.syncStatusUnsubscribe?.();
    this.syncStatusUnsubscribe = subscribeToSync((status) => {
      this.syncStatus = status;
      if (status.error) {
        this.lastSyncErrorAt = new Date();
      }
    });
  }

  private isSyncUnstableForEmptyState(): boolean {
    if (this.syncStatus.isSyncing || Boolean(this.syncStatus.error)) {
      return true;
    }

    if (!this.lastSyncErrorAt) {
      return false;
    }

    return Date.now() - this.lastSyncErrorAt.getTime() <= 30000;
  }

  private handleFabClick = () => {
    if (!this.visitId) return;
    navigate(`/visita/${this.visitId}/nova-nota`);
  };

  private handleTagAction = (e: CustomEvent<{
    tag: string;
    notes: Note[];
    scopeType: 'tag';
  }>) => {
    this.selectedScope = { type: 'tag', tag: e.detail.tag, notes: e.detail.notes };
    this.selectedTitle = e.detail.tag;
    this.isActionSheetOpen = true;
  };

  private handleNoteClick = (e: CustomEvent<{ note: Note }>) => {
    if (!this.visitId) return;
    navigate(`/visita/${this.visitId}/editar-nota/${e.detail.note.id}`);
  };

  private handleActionSelected = async (e: CustomEvent<{ actionId: string }>) => {
    const { actionId } = e.detail;

    if (actionId === 'copy' && this.selectedScope) {
      await this.handleCopyMessage();
      this.isActionSheetOpen = false;
    } else if (actionId === 'preview' && this.selectedScope) {
      this.handlePreviewMessage();
      this.isActionSheetOpen = false;
    } else if (actionId === 'share' && this.selectedScope) {
      await this.handleShareMessage();
      this.isActionSheetOpen = false;
    } else if (actionId === 'delete' && this.selectedScope?.type === 'tag') {
      this.isActionSheetOpen = false;
      this.isDeleteConfirmOpen = true;
    }
  };

  private buildExportScope(): ExportScope | null {
    if (!this.selectedScope) return null;

    if (this.selectedScope.type === 'tag') {
      return { type: 'tag', tag: this.selectedScope.tag, notes: this.selectedScope.notes };
    }

    const groupedTags = groupNotesByTag(this.selectedScope.notes);

    if (groupedTags.length === 0) {
      return null;
    }

    return {
      type: 'date',
      date: this.currentVisit?.date ?? '',
      title: this.currentVisit?.name ?? 'Pendências',
      tags: groupedTags.map((group) => ({
        tag: group.tag,
        notes: group.notes,
      })),
    };
  }

  private async handleCopyMessage(): Promise<void> {
    const scope = this.buildExportScope();
    if (!scope) return;

    const message = generateMessage(scope);
    const success = await copyToClipboard(message);

    if (success) {
      this.showTemporaryToast('Mensagem copiada');
    }
  }

  private handleShareMessage = async (): Promise<void> => {
    const scope = this.buildExportScope();
    if (!scope) return;

    const message = generateMessage(scope);

    // Feature detection: verificar se navigator.share está disponível
    const canShare = 'share' in navigator && typeof navigator.share === 'function';

    if (canShare) {
      try {
        await navigator.share({ text: message });
        return; // Sucesso - sem toast
      } catch {
        // Usuário cancelou ou erro - fallback para copiar
      }
    }

    // Fallback: copiar para clipboard
    const success = await copyToClipboard(message);
    if (success) {
      this.showTemporaryToast('Mensagem copiada');
    }
  };

  private handlePreviewMessage(): void {
    const scope = this.buildExportScope();
    if (!scope) return;

    this.previewMessage = generateMessage(scope);
    this.isPreviewOpen = true;
  }

  private handlePreviewCopy = async (): Promise<void> => {
    const success = await copyToClipboard(this.previewMessage);

    if (success) {
      this.showTemporaryToast('Mensagem copiada');
    }
  };

  private handlePreviewClose = (): void => {
    this.isPreviewOpen = false;
    this.previewMessage = '';
  };

  private getNoteIdsToDelete(): string[] {
    if (!this.selectedScope || this.selectedScope.type !== 'tag') return [];
    return this.selectedScope.notes.map((note) => note.id);
  }

  private handleDeleteConfirm = async (): Promise<void> => {
    const noteIds = this.getNoteIdsToDelete();

    if (noteIds.length === 0) {
      this.isDeleteConfirmOpen = false;
      return;
    }

    try {
      await deleteNotes(noteIds);
      await this.loadVisit();

      if (this.isVisitExpired) {
        this.showTemporaryToast('Visita expirada localmente');
      } else {
        this.showTemporaryToast(`${String(noteIds.length)} nota(s) excluída(s)`);
      }
    } catch (error) {
      console.error('Erro ao excluir notas:', error);
      this.showTemporaryToast('Erro ao excluir notas');
    } finally {
      this.isDeleteConfirmOpen = false;
      this.selectedScope = null;
    }
  };

  private handleDeleteCancel = (): void => {
    this.isDeleteConfirmOpen = false;
    this.selectedScope = null;
  };

  private canDeletePrivateVisit(): boolean {
    return this.currentVisit?.mode === 'private' && this.member?.role === 'owner' && this.accessState === 'active';
  }

  private canDeleteGroupVisitForAll(): boolean {
    return this.currentVisit?.mode === 'group' && this.member?.role === 'owner' && this.accessState === 'active';
  }

  private canLeaveGroupVisit(): boolean {
    const role = this.member?.role;

    return (
      this.currentVisit?.mode === 'group'
      && this.accessState === 'active'
      && role !== undefined
      && role !== 'owner'
    );
  }

  private canOpenParticipantsPanel(): boolean {
    if (!this.currentVisit) return false;
    return canOpenParticipantsPanel(this.member, this.currentVisit.mode);
  }

  private canInvitePeople(): boolean {
    if (!this.currentVisit || this.accessState !== 'active' || !this.member) {
      return false;
    }

    // Quem gerencia convites (owner/admin ativos) pode convidar, inclusive com papel admin
    return this.member.role === 'owner' || this.member.role === 'admin';
  }

  private canCreatePrivateCopy(): boolean {
    if (!this.currentVisit || !this.member) {
      return false;
    }

    return this.currentVisit.mode === 'group' && canDuplicateVisit(this.member);
  }

  private canExportWholeVisitMessage(): boolean {
    return this.accessState === 'active' && this.notes.length > 0;
  }

  private getVisitModeLabel(): string {
    return this.currentVisit?.mode === 'group' ? 'Compartilhada' : 'Privada';
  }

  private getVisitModeBadgeClass(): string {
    return this.currentVisit?.mode === 'group' ? 'text-bg-primary' : 'text-bg-secondary';
  }

  private formatDateTimeForDisplay(date: Date): string {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private getVisitLastUpdatedLabel(): string {
    if (!this.currentVisit) {
      return '';
    }

    const lastUpdate = this.currentVisit.updatedAt ?? this.currentVisit.createdAt;
    return `Atualizada em ${this.formatDateTimeForDisplay(lastUpdate)}`;
  }

  private getActionSheetActions(): DashboardAction[] {
    if (this.selectedScope?.type === 'visit') {
      return getDashboardGroupActions(false);
    }

    return this.actions;
  }

  private handleWholeVisitMessageClick = (): void => {
    if (!this.canExportWholeVisitMessage()) {
      this.showTemporaryToast('Sem notas para exportar');
      return;
    }

    this.selectedScope = {
      type: 'visit',
      notes: this.notes,
    };
    this.selectedTitle = 'Visita inteira';
    this.isActionSheetOpen = true;
  };

  private handleDuplicateVisitClick = async (): Promise<void> => {
    if (!this.visitId || this.isDuplicatingVisit || !this.canCreatePrivateCopy()) {
      return;
    }

    this.isDuplicatingVisit = true;

    try {
      const duplicatedVisit = await duplicateVisitAsPrivate(this.visitId);
      this.showTemporaryToast('Cópia privada criada');
      navigate(`/visita/${duplicatedVisit.id}`);
    } catch (error) {
      console.error('Erro ao criar cópia privada da visita:', error);
      this.showTemporaryToast('Erro ao criar cópia privada');
    } finally {
      this.isDuplicatingVisit = false;
    }
  };

  private handleInvitePeopleClick = (): void => {
    this.inviteRole = 'editor';
    this.inviteLink = '';
    this.isGeneratingInvite = false;
    this.isInviteModalOpen = true;
  };

  private handleInviteModalClose = (): void => {
    this.isInviteModalOpen = false;
  };

  private handleInviteRoleChange = (event: Event): void => {
    const select = event.target as HTMLSelectElement;
    if (select.value === 'admin' || select.value === 'editor' || select.value === 'viewer') {
      this.inviteRole = select.value;
    }
  };

  private handleGenerateInviteLink = async (): Promise<void> => {
    if (!this.visitId || this.isGeneratingInvite) {
      return;
    }

    this.isGeneratingInvite = true;

    try {
      // ensureVisitIsGroup exige ownership (só owner converte private→group);
      // admin ativo já participa de visita group, então a promoção é pulada para ele.
      if (this.currentVisit?.mode !== 'group') {
        this.currentVisit = await ensureVisitIsGroup(this.visitId);
      }
      const invite = await createVisitInviteForVisit({
        visitId: this.visitId,
        role: this.inviteRole,
      });

      this.inviteLink = buildVisitInviteLink(invite.token);
      this.showTemporaryToast('Link de convite gerado');
    } catch (error) {
      console.error('Erro ao gerar link de convite:', error);
      this.showTemporaryToast('Erro ao gerar link de convite');
    } finally {
      this.isGeneratingInvite = false;
    }
  };

  private handleCopyInviteLink = async (): Promise<void> => {
    if (!this.inviteLink) {
      return;
    }

    const success = await copyToClipboard(this.inviteLink);
    if (success) {
      this.showTemporaryToast('Link copiado');
    }
  };

  private handleShareInviteLink = async (): Promise<void> => {
    if (!this.inviteLink) {
      return;
    }

    const canShare = 'share' in navigator && typeof navigator.share === 'function';

    if (canShare) {
      try {
        await navigator.share({
          title: 'Convite para visita',
          text: this.inviteLink,
          url: this.inviteLink,
        });
        return;
      } catch {
        // Fallback para clipboard
      }
    }

    const success = await copyToClipboard(this.inviteLink);
    if (success) {
      this.showTemporaryToast('Link copiado');
    }
  };

  private handleParticipantsClick = (): void => {
    this.isParticipantsModalOpen = true;
    void this.loadParticipants();
  };

  private handleParticipantsModalClose = (): void => {
    if (this.isRemovingParticipant || this.isChangingParticipantRole) {
      return;
    }

    this.isParticipantsModalOpen = false;
  };

  private async loadParticipants(): Promise<void> {
    if (!this.visitId) return;

    this.isLoadingParticipants = true;

    try {
      await this.refreshParticipantsList();
    } finally {
      this.isLoadingParticipants = false;
    }
  }

  private async refreshParticipantsList(): Promise<void> {
    if (!this.visitId) return;

    try {
      this.participants = await fetchVisitMembersFromRemote(this.visitId);
      this.participantsFromCache = false;
    } catch (error) {
      // Erro de rede/Firestore: fallback para cache local com aviso
      console.error('Erro ao buscar participantes remotos:', error);
      this.participants = await listVisitMembers(this.visitId);
      this.participantsFromCache = true;
    }
  }

  private handleParticipantRoleClick = (target: VisitMember, action: 'promote' | 'demote'): void => {
    if (this.isChangingParticipantRole || this.isRemovingParticipant) {
      return;
    }

    this.roleChangeTarget = { member: target, action };
    this.roleChangeError = '';
  };

  private handleRoleChangeCancel = (): void => {
    if (this.isChangingParticipantRole) {
      return;
    }

    this.roleChangeTarget = null;
    this.roleChangeError = '';
  };

  private handleParticipantRoleChange = async (): Promise<void> => {
    if (!this.visitId || !this.roleChangeTarget || this.isChangingParticipantRole || this.isRemovingParticipant) {
      return;
    }

    const target = this.roleChangeTarget;
    const newRole = target.action === 'promote' ? 'admin' : 'editor';
    this.isChangingParticipantRole = true;
    this.roleChangeError = '';

    try {
      const result = await updateVisitMemberRole(this.visitId, target.member.userId, newRole);

      if (result.status === 'updated') {
        this.roleChangeTarget = null;
        await this.refreshParticipantsList();
        this.showTemporaryToast(target.action === 'promote' ? 'Promovido a admin' : 'Rebaixado a editor');
      } else {
        this.roleChangeError = this.getParticipantManagementError(result.status);
      }
    } catch (error) {
      console.error('Erro ao alterar papel do participante:', error);
      this.roleChangeError = this.getParticipantNetworkError();
    } finally {
      this.isChangingParticipantRole = false;
    }
  };

  private handleParticipantRemoveClick = (target: VisitMember): void => {
    if (this.isChangingParticipantRole || this.isRemovingParticipant) {
      return;
    }

    this.removeParticipantTarget = target;
    this.removeParticipantError = '';
  };

  private handleRemoveParticipantCancel = (): void => {
    if (this.isRemovingParticipant) {
      return;
    }

    this.removeParticipantTarget = null;
    this.removeParticipantError = '';
  };

  private handleRemoveParticipantConfirm = async (): Promise<void> => {
    if (!this.visitId || !this.removeParticipantTarget || this.isRemovingParticipant) {
      return;
    }

    const target = this.removeParticipantTarget;
    this.isRemovingParticipant = true;
    this.removeParticipantError = '';

    try {
      const result = await removeVisitMemberAsAdmin(this.visitId, target.userId);

      if (result.status === 'removed') {
        this.removeParticipantTarget = null;
        await this.refreshParticipantsList();
        this.showTemporaryToast('Participante removido');
      } else {
        this.removeParticipantError = this.getParticipantManagementError(result.status);
      }
    } catch (error) {
      console.error('Erro ao remover participante:', error);
      this.removeParticipantError = this.getParticipantNetworkError();
    } finally {
      this.isRemovingParticipant = false;
    }
  };

  private getParticipantManagementError(
    status: RemoveVisitMemberResult['status'] | UpdateVisitMemberRoleResult['status']
  ): string {
    switch (status) {
      case 'forbidden':
        return 'Você não tem permissão para executar esta ação.';
      case 'target-not-found':
        return 'Participante não encontrado. Atualize a lista e tente novamente.';
      case 'cannot-remove-owner':
      case 'cannot-update-owner':
        return 'O dono da visita não pode ser alterado.';
      case 'cannot-remove-self':
      case 'cannot-update-self':
        return 'Você não pode executar esta ação em você mesmo.';
      default:
        return '';
    }
  }

  private getParticipantNetworkError(): string {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  }

  private handleVisitDeleteClick = (): void => {
    this.visitDeleteError = '';
    this.isVisitDeleteConfirmOpen = true;
  };

  private handleVisitDeleteCancel = (): void => {
    if (this.isDeletingVisit) {
      return;
    }

    this.visitDeleteError = '';
    this.isVisitDeleteConfirmOpen = false;
  };

  private handleVisitDeleteConfirm = async (): Promise<void> => {
    if (this.isDeletingVisit) {
      return;
    }

    if (!this.visitId) {
      this.isVisitDeleteConfirmOpen = false;
      return;
    }

    this.isDeletingVisit = true;
    this.visitDeleteError = '';

    try {
      if (this.canDeletePrivateVisit()) {
        await deletePrivateVisit(this.visitId);
      } else if (this.canDeleteGroupVisitForAll()) {
        await deleteGroupVisitAsOwner(this.visitId);
      }
      this.isVisitDeleteConfirmOpen = false;
      this.showTemporaryToast('Visita excluída');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro ao excluir visita:', error);
      this.visitDeleteError = 'Não foi possível concluir a ação. Tente novamente em alguns instantes.';
    } finally {
      this.isDeletingVisit = false;
    }
  };

  private handleLeaveVisitClick = (): void => {
    this.leaveVisitError = '';
    this.isLeaveVisitConfirmOpen = true;
  };

  private handleLeaveVisitCancel = (): void => {
    if (this.isLeavingVisit) {
      return;
    }

    this.leaveVisitError = '';
    this.isLeaveVisitConfirmOpen = false;
  };

  private handleLeaveVisitConfirm = async (): Promise<void> => {
    if (this.isLeavingVisit) {
      return;
    }

    if (!this.visitId) {
      this.isLeaveVisitConfirmOpen = false;
      return;
    }

    this.isLeavingVisit = true;
    this.leaveVisitError = '';

    try {
      await leaveVisit(this.visitId);
      this.isLeaveVisitConfirmOpen = false;
      this.showTemporaryToast('Você saiu da visita');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro ao sair da visita:', error);
      this.leaveVisitError = 'Não foi possível concluir a ação. Tente novamente em alguns instantes.';
    } finally {
      this.isLeavingVisit = false;
    }
  };

  private showTemporaryToast(message: string): void {
    this.toastMessage = message;
    this.showToast = true;

    setTimeout(() => {
      this.showToast = false;
    }, 2000);
  }

  private handleSheetClosed = () => {
    this.isActionSheetOpen = false;
  };

  private isUserRemoved(): boolean {
    return this.accessState === 'removed';
  }

  private isUserNoMembership(): boolean {
    return this.accessState === 'no-membership';
  }

  private canUserEditNote(): boolean {
    if (!this.member) return false;
    return canEditNote(this.member);
  }

  private renderAccessRemoved() {
    return html`
      <app-header title="Acesso removido"></app-header>
      <main class="container-fluid wf-page-container wf-with-header wf-sheet-safe pb-4">
        <div class="d-flex align-items-center justify-content-center" style="min-height: 55vh;">
          <div class="card border-0 shadow-sm text-center w-100" style="max-width: 420px;">
            <div class="card-body p-4">
              <svg class="mx-auto text-secondary opacity-75 mb-3" width="56" height="56" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <p class="h6 mb-2">Acesso removido</p>
              <p class="text-secondary mb-3">Seu acesso a esta visita foi removido.</p>
              <button type="button" class="btn btn-primary" @click=${this.handleBackClick}>
                Ir para minhas visitas
              </button>
            </div>
          </div>
        </div>
      </main>
    `;
  }

  private renderAccessDenied() {
    return html`
      <app-header title="Visita indisponível"></app-header>
      <main class="container-fluid wf-page-container wf-with-header wf-sheet-safe pb-4">
        <div class="d-flex align-items-center justify-content-center" style="min-height: 55vh;">
          <div class="card border-0 shadow-sm text-center w-100" style="max-width: 420px;">
            <div class="card-body p-4">
              <svg class="mx-auto text-secondary opacity-75 mb-3" width="56" height="56" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p class="h6 mb-2">Visita indisponível</p>
              <p class="text-secondary mb-3">Esta visita pode ter expirado ou você não tem mais acesso.</p>
              <button type="button" class="btn btn-outline-secondary" @click=${this.handleBackClick}>
                Voltar
              </button>
            </div>
          </div>
        </div>
      </main>
    `;
  }

  private renderVisitExpired() {
    return html`
      <app-header title="Visita expirada"></app-header>
      <main class="container-fluid wf-page-container wf-with-header wf-sheet-safe pb-4">
        <div class="d-flex align-items-center justify-content-center" style="min-height: 55vh;">
          <div class="card border-0 shadow-sm text-center w-100" style="max-width: 420px;">
            <div class="card-body p-4">
              <svg class="mx-auto text-secondary opacity-75 mb-3" width="56" height="56" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m2 9H7a2 2 0 01-2-2V7a2 2 0 012-2h2m8 0h-2m2 0v2m0-2a2 2 0 012 2v2" />
              </svg>
              <p class="h6 mb-2">Visita expirada</p>
              <p class="text-secondary mb-3">Esta visita expirou localmente e não está mais disponível.</p>
              <button type="button" class="btn btn-primary" @click=${this.handleBackClick}>
                Ir para minhas visitas
              </button>
            </div>
          </div>
        </div>
      </main>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="d-flex align-items-center justify-content-center" style="min-height: 55vh;">
        <div class="card border-0 shadow-sm text-center w-100" style="max-width: 420px;">
          <div class="card-body p-4">
            <svg class="mx-auto text-secondary opacity-75 mb-3" width="56" height="56" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p class="h6 mb-2">Nenhuma nota ainda</p>
            <p class="text-secondary mb-3">Comece criando uma nova nota para registrar suas observações clínicas.</p>
            <div class="small text-secondary border-top pt-3">Toque no botão abaixo para adicionar</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderSyncUnstableEmptyState() {
    return html`
      <div class="d-flex align-items-center justify-content-center" style="min-height: 55vh;">
        <div class="card border-0 shadow-sm text-center w-100" style="max-width: 420px;">
          <div class="card-body p-4">
            <svg class="mx-auto text-warning opacity-75 mb-3" width="56" height="56" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.66 18h16.68a1 1 0 00.87-1.5l-7.5-13a1 1 0 00-1.74 0z" />
            </svg>
            <p class="h6 mb-2">Sincronização incompleta</p>
            <p class="text-secondary mb-3">Conexão instável. Mantendo dados locais enquanto sincronizamos.</p>
            <div class="small text-secondary border-top pt-3">Tentando reconectar e atualizar esta visita</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderNotesList() {
    const groupedNotes = groupNotesByTag(this.notes);

    return html`
      <div class="d-flex flex-column gap-3" @note-click=${this.handleNoteClick}>
        ${groupedNotes.map(
          (group) => html`
            <div class="card border-0 shadow-sm">
              <tag-group .tag=${group.tag} .notes=${group.notes} @tag-action=${this.handleTagAction}></tag-group>
            </div>
          `
        )}
      </div>
    `;
  }

  private renderDashboardContent() {
    // S12A: título fixo do app na navbar
    const title = 'VisitaMed';

    return html`
      <app-header title=${title} ?showBack=${true} @back-click=${this.handleBackClick}></app-header>
      <sync-status-bar></sync-status-bar>

      <main class="container-fluid wf-page-container wf-with-header-sync wf-sheet-safe pb-4">
        ${this.currentVisit
          ? html`
              <div class="card border-0 shadow-sm mb-3">
                <div class="card-body d-flex flex-column gap-3">
                  <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <h2 class="h5 mb-0">${this.currentVisit.name}</h2>
                        <span class="badge ${this.getVisitModeBadgeClass()}">${this.getVisitModeLabel()}</span>
                      </div>
                      <div class="small text-secondary mt-1">${this.getVisitLastUpdatedLabel()}</div>
                    </div>

                    <button
                      type="button"
                      class="btn btn-primary"
                      ?disabled=${!this.canExportWholeVisitMessage()}
                      @click=${this.handleWholeVisitMessageClick}
                    >
                      Gerar mensagem da visita
                    </button>
                  </div>

                  ${!this.canExportWholeVisitMessage()
                    ? html`<div class="small text-secondary">Adicione notas para gerar a mensagem da visita.</div>`
                    : ''}
                </div>
              </div>
            `
          : ''}

        ${this.canCreatePrivateCopy() || this.canInvitePeople() || this.canDeletePrivateVisit() || this.canDeleteGroupVisitForAll() || this.canLeaveGroupVisit() || this.canOpenParticipantsPanel()
          ? html`
              <div class="mb-3 d-flex justify-content-end gap-2 flex-wrap">
                ${this.canCreatePrivateCopy()
                  ? html`
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        ?disabled=${this.isDuplicatingVisit}
                        @click=${this.handleDuplicateVisitClick}
                      >
                        ${this.isDuplicatingVisit ? 'Criando cópia...' : 'Criar cópia privada'}
                      </button>
                    `
                  : ''}
                ${this.canOpenParticipantsPanel()
                  ? html`
                      <button type="button" class="btn btn-outline-secondary" @click=${this.handleParticipantsClick}>
                        Participantes
                      </button>
                    `
                  : ''}
                ${this.canInvitePeople()
                  ? html`
                      <button type="button" class="btn btn-outline-primary" @click=${this.handleInvitePeopleClick}>
                        Convidar pessoas
                      </button>
                    `
                  : ''}
                ${this.canDeletePrivateVisit()
                  ? html`
                      <button type="button" class="btn btn-outline-danger" @click=${this.handleVisitDeleteClick}>
                        Excluir visita
                      </button>
                    `
                  : ''}
                ${this.canDeleteGroupVisitForAll()
                  ? html`
                      <button type="button" class="btn btn-outline-danger" @click=${this.handleVisitDeleteClick}>
                        Excluir visita para todos
                      </button>
                    `
                  : ''}
                ${this.canLeaveGroupVisit()
                  ? html`
                      <button type="button" class="btn btn-outline-secondary" @click=${this.handleLeaveVisitClick}>
                        Sair da visita
                      </button>
                    `
                  : ''}
              </div>
            `
          : ''}

        ${this.isLoading
          ? html`<div class="d-flex align-items-center justify-content-center text-secondary" style="min-height: 50vh;">Carregando...</div>`
          : this.notes.length > 0
            ? this.renderNotesList()
            : this.isSyncUnstableForEmptyState()
              ? this.renderSyncUnstableEmptyState()
              : this.renderEmptyState()}
      </main>

      ${this.canUserEditNote()
        ? html`<fab-button icon="plus" label="Nova nota" @fab-click=${this.handleFabClick}></fab-button>`
        : ''}
    `;
  }

  private handleBackClick = () => {
    navigate('/dashboard');
  };

  private renderPreview() {
    return html`
      <app-header title="VisitaMed"></app-header>

      <main class="container-fluid wf-page-container wf-with-header pb-4">
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-header bg-body fw-semibold">Pré-visualizar mensagem</div>
          <div class="card-body">
            <pre class="wf-preview-message">${this.previewMessage}</pre>
          </div>
        </div>

        <div class="d-grid gap-2 d-sm-flex justify-content-end">
          <button type="button" class="btn btn-outline-secondary" @click=${this.handlePreviewClose}>
            Fechar
          </button>
          <button type="button" class="btn btn-primary" @click=${this.handlePreviewCopy}>
            Copiar
          </button>
        </div>
      </main>
    `;
  }

  private renderToast() {
    return html`
      <div
        class="position-fixed start-50 translate-middle-x text-bg-dark rounded-pill px-3 py-2 small shadow ${
          this.showToast ? '' : 'd-none'
        }"
        style="bottom: calc(80px + var(--safe-area-inset-bottom)); z-index: var(--z-toast);"
      >
        ${this.toastMessage}
      </div>
    `;
  }

  override render() {
    if (this.isVisitExpired) {
      return this.renderVisitExpired();
    }

    // Usuário removido da visita - mensagem específica
    if (this.isUserRemoved()) {
      return this.renderAccessRemoved();
    }

    // Sem membership - visita indisponível
    if (this.isUserNoMembership()) {
      return this.renderAccessDenied();
    }

    return html`
      ${this.isPreviewOpen ? this.renderPreview() : this.renderDashboardContent()}

      <action-sheet
        .visible=${this.isActionSheetOpen}
        .title=${this.selectedTitle}
        .actions=${this.getActionSheetActions()}
        @action-selected=${this.handleActionSelected}
        @sheet-closed=${this.handleSheetClosed}
      ></action-sheet>

      ${this.renderToast()}
      ${this.renderDeleteConfirm()}
      ${this.renderVisitDeleteConfirm()}
      ${this.renderLeaveVisitConfirm()}
      ${this.renderInviteModal()}
      ${this.renderParticipantsModal()}
      ${this.renderRemoveParticipantConfirm()}
      ${this.renderRoleChangeConfirm()}
    `;
  }

  private renderDeleteConfirm() {
    if (!this.isDeleteConfirmOpen) return null;

    const count = this.getNoteIdsToDelete().length;
    const scopeLabel = this.selectedScope?.type === 'tag' ? 'desta tag' : '';

    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${this.handleDeleteCancel}>
        <div class="modal-dialog modal-dialog-centered modal-sm" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4">
              <h3 class="h6 mb-2">Excluir notas?</h3>
              <p class="text-secondary mb-3">${count} nota(s) ${scopeLabel} serão excluídas permanentemente.</p>
              <div class="d-grid gap-2 d-sm-flex justify-content-end">
                <button type="button" class="btn btn-outline-secondary" @click=${this.handleDeleteCancel}>
                  Cancelar
                </button>
                <button type="button" class="btn btn-danger" @click=${this.handleDeleteConfirm}>
                  Excluir notas
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderVisitDeleteConfirm() {
    if (!this.isVisitDeleteConfirmOpen) return null;

    const isGroupDelete = this.canDeleteGroupVisitForAll();
    const isProcessing = this.isDeletingVisit;
    const title = isGroupDelete ? 'Excluir visita para todos?' : 'Excluir visita?';
    const body = isGroupDelete
      ? 'Esta visita colaborativa será removida para todos os membros.'
      : 'Esta visita privada e todas as suas notas serão excluídas.';
    const actionLabel = isGroupDelete ? 'Excluir visita para todos' : 'Excluir visita';

    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${this.handleVisitDeleteCancel}>
        <div class="modal-dialog modal-dialog-centered modal-sm" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4">
              <h3 class="h6 mb-2">${title}</h3>
              <p class="text-secondary mb-3">${body}</p>
              ${this.visitDeleteError
                ? html`<div class="alert alert-danger py-2 px-3 small" role="alert">${this.visitDeleteError}</div>`
                : ''}
              <div class="d-grid gap-2 d-sm-flex justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  ?disabled=${isProcessing}
                  @click=${this.handleVisitDeleteCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  ?disabled=${isProcessing}
                  @click=${this.handleVisitDeleteConfirm}
                >
                  ${isProcessing
                    ? html`<span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Excluindo...`
                    : actionLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderLeaveVisitConfirm() {
    if (!this.isLeaveVisitConfirmOpen) return null;

    const isProcessing = this.isLeavingVisit;

    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${this.handleLeaveVisitCancel}>
        <div class="modal-dialog modal-dialog-centered modal-sm" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4">
              <h3 class="h6 mb-2">Sair da visita?</h3>
              <p class="text-secondary mb-3">Você perderá acesso a esta visita.</p>
              ${this.leaveVisitError
                ? html`<div class="alert alert-danger py-2 px-3 small" role="alert">${this.leaveVisitError}</div>`
                : ''}
              <div class="d-grid gap-2 d-sm-flex justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  ?disabled=${isProcessing}
                  @click=${this.handleLeaveVisitCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="btn btn-danger"
                  ?disabled=${isProcessing}
                  @click=${this.handleLeaveVisitConfirm}
                >
                  ${isProcessing
                    ? html`<span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Saindo...`
                    : 'Sair da visita'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderInviteModal() {
    if (!this.isInviteModalOpen) return null;

    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${this.handleInviteModalClose}>
        <div class="modal-dialog modal-dialog-centered" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4 d-flex flex-column gap-3">
              <div>
                <h3 class="h5 mb-1">Convidar pessoas</h3>
                <p class="text-secondary mb-0">Escolha o nível de acesso para o convite.</p>
              </div>

              <div>
                <label for="invite-role-select" class="form-label">Nível de acesso</label>
                <select
                  id="invite-role-select"
                  class="form-select"
                  .value=${this.inviteRole}
                  @change=${this.handleInviteRoleChange}
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Leitor</option>
                </select>
                <div class="small text-secondary mt-2">Admin: gerencia participantes e convites.</div>
                <div class="small text-secondary">Editor: pode criar, editar e excluir notas.</div>
                <div class="small text-secondary">Leitor: pode apenas visualizar e exportar.</div>
              </div>

              <div class="d-grid gap-2 d-sm-flex justify-content-end">
                <button type="button" class="btn btn-outline-secondary" @click=${this.handleInviteModalClose}>
                  Fechar
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  ?disabled=${this.isGeneratingInvite}
                  @click=${this.handleGenerateInviteLink}
                >
                  ${this.isGeneratingInvite ? 'Gerando...' : 'Gerar link'}
                </button>
              </div>

              ${this.inviteLink
                ? html`
                    <div class="border-top pt-3 d-flex flex-column gap-2">
                      <label class="form-label mb-0" for="invite-link-input">Link de convite</label>
                      <input id="invite-link-input" class="form-control" .value=${this.inviteLink} readonly />
                      <div class="d-grid gap-2 d-sm-flex justify-content-end">
                        <button type="button" class="btn btn-outline-secondary" @click=${this.handleCopyInviteLink}>
                          Copiar link
                        </button>
                        <button type="button" class="btn btn-primary" @click=${this.handleShareInviteLink}>
                          Compartilhar link
                        </button>
                      </div>
                    </div>
                  `
                : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private getVisibleParticipants(): VisitMember[] {
    const roleOrder: Record<VisitRole, number> = {
      owner: 0,
      admin: 1,
      editor: 2,
      viewer: 3,
    };

    return this.participants
      .filter((member) => member.status === 'active')
      .sort((a, b) => {
        const roleDiff = roleOrder[a.role] - roleOrder[b.role];
        if (roleDiff !== 0) return roleDiff;
        return getParticipantDisplayName(a).localeCompare(getParticipantDisplayName(b), 'pt-BR');
      });
  }

  private renderParticipantsModal() {
    if (!this.isParticipantsModalOpen) return null;

    const isBusy = this.isChangingParticipantRole || this.isRemovingParticipant;

    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${this.handleParticipantsModalClose}>
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4 d-flex flex-column gap-3">
              <div class="d-flex justify-content-between align-items-start gap-3">
                <h3 class="h5 mb-0">Participantes</h3>
                <button
                  type="button"
                  class="btn-close"
                  aria-label="Fechar painel de participantes"
                  ?disabled=${isBusy}
                  @click=${this.handleParticipantsModalClose}
                ></button>
              </div>

              ${this.participantsFromCache
                ? html`
                    <div class="alert alert-warning py-2 px-3 small mb-0" role="alert">
                      Sem conexão — exibindo a lista salva neste dispositivo.
                    </div>
                  `
                : ''}

              ${this.isLoadingParticipants
                ? html`
                    <div class="d-flex align-items-center gap-2 text-secondary py-3">
                      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                      Carregando participantes...
                    </div>
                  `
                : this.getVisibleParticipants().length === 0
                  ? html`<p class="text-secondary mb-0 py-3">Nenhum participante encontrado.</p>`
                  : html`
                      <ul class="list-unstyled d-flex flex-column gap-2 mb-0">
                        ${this.getVisibleParticipants().map((member) => this.renderParticipantRow(member))}
                      </ul>
                    `}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderParticipantRow(member: VisitMember) {
    const currentMember = this.member;
    const actions = currentMember ? getParticipantRowActions(member, currentMember) : [];
    const isBusy = this.isChangingParticipantRole || this.isRemovingParticipant;
    const displayName = getParticipantDisplayName(member);
    const isSelf = currentMember?.userId === member.userId;

    return html`
      <li class="card border-0 shadow-sm">
        <div class="card-body py-3 d-flex flex-column gap-2">
          <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <span class="fw-semibold text-break">
              ${displayName}${isSelf ? html`<span class="text-secondary fw-normal"> (você)</span>` : ''}
            </span>
            <span class="badge text-bg-secondary">${getParticipantRoleLabel(member.role)}</span>
          </div>
          ${actions.length > 0
            ? html`
                <div class="d-flex flex-wrap gap-2">
                  ${actions.map((action) => this.renderParticipantRowAction(member, action, isBusy))}
                </div>
              `
            : ''}
        </div>
      </li>
    `;
  }

  private renderParticipantRowAction(
    member: VisitMember,
    action: ParticipantRowAction,
    isBusy: boolean
  ) {
    if (action.id === 'remove') {
      return html`
        <button
          type="button"
          class="btn btn-outline-danger"
          ?disabled=${isBusy}
          @click=${() => { this.handleParticipantRemoveClick(member); }}
        >
          Remover
        </button>
      `;
    }

    const roleActionId = action.id;
    const actionLabel = roleActionId === 'promote' ? 'Promover a admin' : 'Rebaixar a editor';

    return html`
      <button
        type="button"
        class="btn btn-outline-primary"
        ?disabled=${isBusy}
        @click=${() => { this.handleParticipantRoleClick(member, roleActionId); }}
      >
        ${actionLabel}
      </button>
    `;
  }

  private renderRemoveParticipantConfirm() {
    const target = this.removeParticipantTarget;
    if (!target) return null;

    return this.renderParticipantConfirmModal({
      title: 'Remover participante?',
      description: `${getParticipantDisplayName(target)} perderá o acesso a esta visita imediatamente.`,
      error: this.removeParticipantError,
      isProcessing: this.isRemovingParticipant,
      confirmButtonClass: 'btn btn-danger',
      confirmLabel: 'Remover',
      processingLabel: 'Removendo...',
      onCancel: this.handleRemoveParticipantCancel,
      onConfirm: this.handleRemoveParticipantConfirm,
    });
  }

  private renderRoleChangeConfirm() {
    const target = this.roleChangeTarget;
    if (!target) return null;

    const isPromote = target.action === 'promote';
    const displayName = getParticipantDisplayName(target.member);

    return this.renderParticipantConfirmModal({
      title: isPromote ? 'Promover a admin?' : 'Rebaixar a editor?',
      description: isPromote
        ? `${displayName} terá poderes de gerência de participantes e convites nesta visita.`
        : `${displayName} perderá os poderes de gerência.`,
      error: this.roleChangeError,
      isProcessing: this.isChangingParticipantRole,
      confirmButtonClass: 'btn btn-primary',
      confirmLabel: isPromote ? 'Promover' : 'Rebaixar',
      processingLabel: isPromote ? 'Promovendo...' : 'Rebaixando...',
      onCancel: this.handleRoleChangeCancel,
      onConfirm: this.handleParticipantRoleChange,
    });
  }

  private renderParticipantConfirmModal(config: ParticipantConfirmModalConfig) {
    return html`
      <div class="modal-backdrop fade show"></div>
      <div class="modal d-block" tabindex="-1" @click=${config.onCancel}>
        <div class="modal-dialog modal-dialog-centered modal-sm" @click=${(e: Event) => { e.stopPropagation(); }}>
          <div class="modal-content border-0 shadow">
            <div class="modal-body p-4">
              <h3 class="h6 mb-2">${config.title}</h3>
              <p class="text-secondary mb-3">${config.description}</p>
              ${config.error
                ? html`<div class="alert alert-danger py-2 px-3 small" role="alert">${config.error}</div>`
                : ''}
              <div class="d-grid gap-2 d-sm-flex justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  ?disabled=${config.isProcessing}
                  @click=${config.onCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class=${config.confirmButtonClass}
                  ?disabled=${config.isProcessing}
                  @click=${config.onConfirm}
                >
                  ${config.isProcessing
                    ? html`<span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>${config.processingLabel}`
                    : config.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dashboard-view': DashboardView;
  }
}
